
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Slider } from "@/components/ui/slider";
import { toast } from "sonner";
import { supabase } from "@/lib/supabase";
import { Info } from "lucide-react";

interface PointsConfigModalProps {
  isOpen: boolean;
  onClose: () => void;
  userId: string;
  initialConfig: PointsConfig | null;
  onConfigUpdated: () => Promise<void>;
}

export interface PointsConfig {
  id?: number;
  created_at?: string;
  follow_up_points?: number | null;
  redemption_perc?: number | null;
  user_follow_points?: number | null;
  validity?: number | null;
  cash_redemption_amt?: number | null;
  user_id?: string | null;
}

const PointsConfigModal: React.FC<PointsConfigModalProps> = ({ 
  isOpen, 
  onClose, 
  userId,
  initialConfig,
  onConfigUpdated
}) => {
  const [validity, setValidity] = useState<number | undefined>(undefined);
  const [redemptionPerc, setRedemptionPerc] = useState<number | undefined>(undefined);
  const [followUpPoints, setFollowUpPoints] = useState<number | undefined>(undefined);
  const [userFollowPoints, setUserFollowPoints] = useState<number | undefined>(undefined);
  const [cashRedemptionAmt, setCashRedemptionAmt] = useState<number | undefined>(undefined);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showInfo, setShowInfo] = useState<string | null>(null);
  const [config, setConfig] = useState<PointsConfig | null>(null);
  
  // Fetch the current config when the modal opens
  useEffect(() => {
    if (isOpen && userId) {
      fetchConfig();
    }
  }, [isOpen, userId]);

  // Set form values when initialConfig changes or when config is fetched
  useEffect(() => {
    if (config) {
      setValidity(config.validity || undefined);
      setRedemptionPerc(config.redemption_perc || undefined);
      setFollowUpPoints(config.follow_up_points || undefined);
      setUserFollowPoints(config.user_follow_points || undefined);
      setCashRedemptionAmt(config.cash_redemption_amt || undefined);
    } else if (initialConfig) {
      setValidity(initialConfig.validity || undefined);
      setRedemptionPerc(initialConfig.redemption_perc || undefined);
      setFollowUpPoints(initialConfig.follow_up_points || undefined);
      setUserFollowPoints(initialConfig.user_follow_points || undefined);
      setCashRedemptionAmt(initialConfig.cash_redemption_amt || undefined);
    }
  }, [initialConfig, config]);

  const fetchConfig = async () => {
    try {
      const { data, error } = await supabase
        .from('points_config')
        .select('*')
        .eq('user_id', userId)
        .maybeSingle();
      
      if (error) throw error;
      
      if (data) {
        setConfig(data);
      }
    } catch (error) {
      console.error("Error fetching points configuration:", error);
      toast.error("Failed to load configuration");
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      setIsSubmitting(true);
      
      const configData = {
        validity,
        redemption_perc: redemptionPerc,
        follow_up_points: followUpPoints,
        user_follow_points: userFollowPoints,
        cash_redemption_amt: cashRedemptionAmt,
        user_id: userId
      };
      
      let response;
      
      if (config?.id) {
        // Update existing config
        response = await supabase
          .from('points_config')
          .update(configData)
          .eq('id', config.id);
      } else {
        // Create new config
        response = await supabase
          .from('points_config')
          .insert([configData]);
      }
      
      if (response.error) {
        throw response.error;
      }
      
      toast.success("Points configuration saved successfully!");
      await onConfigUpdated();
      // Refetch the config to display updated values
      await fetchConfig();
      onClose();
    } catch (error) {
      console.error("Error saving points configuration:", error);
      toast.error("Failed to save points configuration");
    } finally {
      setIsSubmitting(false);
    }
  };

  const InfoTooltip = ({ text }: { text: string }) => (
    <div className="absolute z-50 bg-gray-800 text-white p-3 rounded-md shadow-lg max-w-xs">
      {text}
    </div>
  );

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px] bg-gray-900 text-white border-gray-700">
        <DialogHeader>
          <DialogTitle>Configure Points</DialogTitle>
          <DialogDescription className="text-gray-400">
            Configure how points are calculated and redeemed in your system
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 pt-2">
          {/* Validity of points */}
          <div className="grid gap-2">
            <Label htmlFor="validity">Validity of your points (days)</Label>
            <Input 
              id="validity" 
              type="number" 
              min="1"
              value={validity === undefined ? "" : validity} 
              onChange={(e) => setValidity(parseInt(e.target.value) || undefined)} 
              className="bg-gray-800 border-gray-700"
              placeholder="30"
            />
          </div>
          
          {/* Redemption percentage */}
          <div className="grid gap-2">
            <Label htmlFor="redemptionPerc">
              Redemption Percentage ({redemptionPerc || 0}%)
            </Label>
            <Slider 
              id="redemptionPerc"
              min={1}
              max={100}
              step={1}
              value={redemptionPerc !== undefined ? [redemptionPerc] : [10]}
              onValueChange={(value) => setRedemptionPerc(value[0])}
              className="py-4"
            />
          </div>
          
          {/* Follow up share points */}
          <div className="grid gap-2">
            <div className="flex items-center">
              <Label htmlFor="followUpPoints">Follow up share points</Label>
              <Button 
                type="button" 
                variant="ghost" 
                size="icon" 
                className="h-6 w-6 ml-2"
                onClick={() => setShowInfo(showInfo === 'followUp' ? null : 'followUp')}
              >
                <Info className="h-4 w-4" />
              </Button>
              {showInfo === 'followUp' && (
                <InfoTooltip text="Follow up share points are the points that you will provide automatically to users who follows a share and visit your establishment. The points are reimbursed against your products and services." />
              )}
            </div>
            <Input 
              id="followUpPoints" 
              type="number" 
              min="0"
              value={followUpPoints === undefined ? "" : followUpPoints} 
              onChange={(e) => setFollowUpPoints(parseInt(e.target.value) || undefined)} 
              className="bg-gray-800 border-gray-700"
              placeholder="5"
            />
          </div>
          
          {/* Share users follow points */}
          <div className="grid gap-2">
            <div className="flex items-center">
              <Label htmlFor="userFollowPoints">Share users follow points</Label>
              <Button 
                type="button" 
                variant="ghost" 
                size="icon" 
                className="h-6 w-6 ml-2"
                onClick={() => setShowInfo(showInfo === 'userFollow' ? null : 'userFollow')}
              >
                <Info className="h-4 w-4" />
              </Button>
              {showInfo === 'userFollow' && (
                <InfoTooltip text="Share users follow points are the points that you will provide automatically to the user who shares your content, when other users follow the share and visit your establishment." />
              )}
            </div>
            <Input 
              id="userFollowPoints" 
              type="number" 
              min="0"
              value={userFollowPoints === undefined ? "" : userFollowPoints} 
              onChange={(e) => setUserFollowPoints(parseInt(e.target.value) || undefined)} 
              className="bg-gray-800 border-gray-700"
              placeholder="2"
            />
          </div>
          
          {/* Cash redemption amount limit */}
          <div className="grid gap-2">
            <div className="flex items-center">
              <Label htmlFor="cashRedemptionAmt">Cash redemption amount limit</Label>
              <Button 
                type="button" 
                variant="ghost" 
                size="icon" 
                className="h-6 w-6 ml-2"
                onClick={() => setShowInfo(showInfo === 'cashRedemption' ? null : 'cashRedemption')}
              >
                <Info className="h-4 w-4" />
              </Button>
              {showInfo === 'cashRedemption' && (
                <InfoTooltip text="Cash redemption amount limit is the amount of points the users need to reach to be able to withdraw their points as cash, from the app" />
              )}
            </div>
            <Input 
              id="cashRedemptionAmt" 
              type="number" 
              min="0"
              value={cashRedemptionAmt === undefined ? "" : cashRedemptionAmt} 
              onChange={(e) => setCashRedemptionAmt(parseInt(e.target.value) || undefined)} 
              className="bg-gray-800 border-gray-700"
              placeholder="100"
            />
          </div>

          <DialogFooter>
            <Button 
              type="button" 
              variant="outline" 
              onClick={onClose}
              disabled={isSubmitting}
              className="mt-4"
            >
              Cancel
            </Button>
            <Button 
              type="submit"
              disabled={isSubmitting}
              className="bg-yellow-600 hover:bg-yellow-700 mt-4"
            >
              {isSubmitting ? "Saving..." : "Save Configuration"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default PointsConfigModal;
